import { useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { Lock, Loader2, ShieldCheck, ShieldOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { toast } from "sonner";
import {
  getParentLockStatus,
  setParentPin,
  clearParentPin,
  setAccountType,
} from "@/lib/parent-lock.functions";

export function ParentLockCard() {
  const getStatus = useServerFn(getParentLockStatus);
  const setPin = useServerFn(setParentPin);
  const clearPin = useServerFn(clearParentPin);
  const saveType = useServerFn(setAccountType);

  const [loading, setLoading] = useState(true);
  const [hasPin, setHasPin] = useState(false);
  const [accountType, setLocalType] = useState<"adult" | "student">("adult");
  const [currentPin, setCurrentPin] = useState("");
  const [newPin, setNewPin] = useState("");
  const [newPin2, setNewPin2] = useState("");
  const [busy, setBusy] = useState(false);

  const refresh = async () => {
    try {
      const s = await getStatus();
      setHasPin(s.hasPin);
      setLocalType(s.accountType);
    } catch {
      /* ignore */
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (newPin !== newPin2) {
      toast.error("New PINs don't match");
      return;
    }
    setBusy(true);
    try {
      await setPin({ data: { newPin, ...(hasPin ? { currentPin } : {}) } });
      toast.success(hasPin ? "Access PIN updated" : "Access PIN set");
      setHasPin(true);
      setCurrentPin("");
      setNewPin("");
      setNewPin2("");
      // Force re-unlock next time
      if (typeof window !== "undefined") sessionStorage.removeItem("parent_lock_unlocked");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Could not save PIN");
    } finally {
      setBusy(false);
    }
  };

  const handleClear = async () => {
    setBusy(true);
    try {
      await clearPin({ data: { currentPin } });
      toast.success("Access PIN removed");
      setHasPin(false);
      setCurrentPin("");
      if (typeof window !== "undefined") sessionStorage.removeItem("parent_lock_unlocked");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Could not remove PIN");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card id="parent-lock">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          {hasPin ? <Lock className="h-5 w-5 text-primary" /> : <ShieldOff className="h-5 w-5 text-muted-foreground" />}
          Parent lock
        </CardTitle>
        <CardDescription>
          {hasPin
            ? "A PIN is set. The Generator and Essay Reviewer are locked until the PIN is entered. The Tutor (Build Your Own) stays open without a PIN."
            : "Set a PIN so the Generator and Essay Reviewer require it before use. The Tutor stays open so students can keep practising."}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {loading ? (
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        ) : (
          <>
            <div className="space-y-2">
              <Label>Who uses this account?</Label>
              <RadioGroup
                value={accountType}
                onValueChange={async (v) => {
                  const next = v as "adult" | "student";
                  const prev = accountType;
                  setLocalType(next);
                  try {
                    await saveType({ data: { accountType: next } });
                    toast.success(
                      next === "student"
                        ? "Switched to student account. Access lock will apply."
                        : "Switched to adult account. Access lock is off.",
                    );
                  } catch (err) {
                    setLocalType(prev);
                    toast.error(err instanceof Error ? err.message : "Could not save");
                  }
                }}
                className="grid grid-cols-1 gap-2 sm:grid-cols-2"
              >
                <Label className="flex cursor-pointer items-start gap-3 rounded-md border p-3 has-[:checked]:border-primary has-[:checked]:bg-primary/5">
                  <RadioGroupItem value="adult" className="mt-0.5" />
                  <span>
                    <span className="block font-medium">Adult (me)</span>
                    <span className="block text-xs text-muted-foreground">Full access. No PIN required.</span>
                  </span>
                </Label>
                <Label className="flex cursor-pointer items-start gap-3 rounded-md border p-3 has-[:checked]:border-primary has-[:checked]:bg-primary/5">
                  <RadioGroupItem value="student" className="mt-0.5" />
                  <span>
                    <span className="block font-medium">Student</span>
                    <span className="block text-xs text-muted-foreground">Generator and Reviewer require an access PIN.</span>
                  </span>
                </Label>
              </RadioGroup>
            </div>

            {accountType === "student" ? (
              <form onSubmit={handleSave} className="space-y-3 max-w-sm">
            {hasPin && (
              <div>
                <Label htmlFor="current-pin">Current PIN</Label>
                <Input
                  id="current-pin"
                  type="password"
                  inputMode="numeric"
                  pattern="\d{4,8}"
                  maxLength={8}
                  value={currentPin}
                  onChange={(e) => setCurrentPin(e.target.value.replace(/\D/g, ""))}
                  required
                />
              </div>
            )}
            <div>
              <Label htmlFor="new-pin">{hasPin ? "New PIN" : "PIN"}</Label>
              <Input
                id="new-pin"
                type="password"
                inputMode="numeric"
                pattern="\d{4,8}"
                maxLength={8}
                value={newPin}
                onChange={(e) => setNewPin(e.target.value.replace(/\D/g, ""))}
                placeholder="4–8 digits"
                required
              />
            </div>
            <div>
              <Label htmlFor="new-pin2">Confirm {hasPin ? "new " : ""}PIN</Label>
              <Input
                id="new-pin2"
                type="password"
                inputMode="numeric"
                pattern="\d{4,8}"
                maxLength={8}
                value={newPin2}
                onChange={(e) => setNewPin2(e.target.value.replace(/\D/g, ""))}
                required
              />
            </div>
            <div className="flex flex-wrap gap-2">
              <Button type="submit" size="sm" disabled={busy || newPin.length < 4}>
                {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <ShieldCheck className="mr-2 h-4 w-4" />}
                {hasPin ? "Update PIN" : "Set PIN"}
              </Button>
              {hasPin && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={handleClear}
                  disabled={busy || currentPin.length < 4}
                >
                  Remove PIN
                </Button>
              )}
            </div>
            </form>
            ) : (
              <p className="text-sm text-muted-foreground">
                Adult accounts have full access. Switch to a student account above to enable the access PIN.
              </p>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
